import apiClient from '@/utility/apiClient'
import { handleApiError } from '@/lib/api/utils/errorHandler'
import { Tag, PageMeta, SearchResult } from '@/lib/api/types/api'

/**
 * Category and tag-related API services
 */
export const CategoryService = {
  /**
   * Get all available tags/categories
   */
  getAllTags: async (): Promise<Tag[]> => {
    try {
      const url = 'tags'
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get tags by IDs
   * Note: The API may return all tags instead of filtering, so we filter client-side
   */
  getTagsByIds: async (tagIds: string[]): Promise<Tag[]> => {
    try {
      if (!tagIds.length) {
        return []
      }

      const params: { 'ids[]': string[] } = { 'ids[]': tagIds }

      const url = 'tags'
      const response = await apiClient.request('GET', url, {
        params: params as unknown as Record<string, string>,
      })

      // Convert string IDs to numbers for proper comparison
      const requestedIds = tagIds.map((id) => parseInt(id, 10))

      // Filter the response to only include requested tags
      // This handles the case where the API returns all tags instead of filtering
      const filteredTags = response.data.filter((tag: Tag) => requestedIds.includes(tag.id))

      return filteredTags
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get content by tag
   * @param tagId Tag ID
   * @param companyId Company ID
   * @param page Page number (optional)
   * @param limit Number of items per page (optional)
   */
  getContentByTag: async (
    tagId: string,
    companyId: string,
    page: number = 1,
    limit: number = 10,
  ): Promise<{ records: SearchResult[]; pageMeta: PageMeta }> => {
    try {
      const params = {
        tag_id: tagId,
        companyId,
        page,
        limit,
      }

      const url = 'contents'
      const response = await apiClient.request('GET', url, { params })

      return {
        records: response.data.records,
        pageMeta: response.data.page_meta,
      }
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get tag by ID
   * Implements the GET /api/tags/:id endpoint
   * @param tagId The ID of the tag to retrieve
   */
  getTagById: async (tagId: string): Promise<Tag> => {
    try {
      const url = `tags/${tagId}`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get stories by tag slug
   * Implements the GET /api/stories?tagSlug=:slug&page=:page endpoint
   * @param tagSlug The slug of the tag to filter by
   * @param page Page number (optional, defaults to 1)
   */
  getStoriesByTag: async (
    tagSlug: string,
    page: number = 1,
  ): Promise<{ records: SearchResult[]; pageMeta: PageMeta }> => {
    try {
      const params = {
        tagSlug,
        page,
      }

      const url = 'stories'
      const response = await apiClient.request('GET', url, { params })

      return {
        records: response.data.records || [],
        pageMeta: response.data.pageMeta || {
          currentPage: page,
          totalPages: 1,
          totalContent: response.data.records?.length || 0,
        },
      }
    } catch (error) {
      handleApiError(error)
    }
  },
}
